"use client";

import { CheckIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { Button } from "./button";
import { Switch } from "./switch";

// Design spec §6.8: the settings form. Two columns (form stack, 280 px help aside) up to
// 1240 px, one below 1024; hairline section cards 24 px in with a 40 px icon tile; toggle rows
// on --border-subtle rules; a sticky save dock while the form is dirty.

export type SettingsLayoutProps = React.ComponentProps<"div"> & {
  /** The right column (`SettingsAside`). */
  aside?: React.ReactNode;
};

export function SettingsLayout({ aside, className, children, ...props }: SettingsLayoutProps) {
  return (
    <div
      data-slot="settings-layout"
      className={cn(
        "grid max-w-[1240px] items-start gap-8 lg:grid-cols-[minmax(0,1fr)_280px]",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-6">{children}</div>
      {aside}
    </div>
  );
}

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type SettingsSectionProps = Omit<React.ComponentProps<"section">, "title"> & {
  /** Anchor for sidebar deep links (`#friday-report`). */
  id?: string;
  title: React.ReactNode;
  icon?: IconType;
  description?: React.ReactNode;
  actions?: React.ReactNode;
};

export function SettingsSection({
  id,
  title,
  icon: Icon,
  description,
  actions,
  className,
  children,
  ...props
}: SettingsSectionProps) {
  const headingId = React.useId();
  return (
    <section
      data-slot="settings-section"
      id={id}
      aria-labelledby={headingId}
      className={cn(
        "scroll-mt-6 rounded-lg border border-border bg-card p-6 text-card-foreground max-md:p-4",
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3.5 max-md:flex-wrap">
        {Icon ? (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary [&_svg]:size-[18px]">
            <Icon aria-hidden={true} />
          </span>
        ) : null}
        <div className="min-w-0 flex-1 max-md:basis-48">
          <h2
            id={headingId}
            className="font-heading text-[17px] leading-6 font-title tracking-[-0.3px] text-foreground"
          >
            {title}
          </h2>
          {description ? (
            <p className="mt-0.5 text-sm leading-[1.65] text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children ? <div className="mt-5 flex flex-col gap-5">{children}</div> : null}
    </section>
  );
}

// Setup UX pass 2026-10-21 (Linear / Vercel settings): a compact card of rows, each a label on
// the left and its control on the right, instead of a section card with a description paragraph.

export type SettingsGroupProps = Omit<React.ComponentProps<"section">, "title"> & {
  /** One 13 px / 600 line over the rows (optional; a group may be rows only). */
  title?: React.ReactNode;
  count?: number;
  /** Right of the title: a small button or a count. */
  actions?: React.ReactNode;
  /** Anchor for sidebar deep links. */
  id?: string;
};

/** A hairline card (radius 11) of `SettingsRow`s, with an optional 40 px title line. */
export function SettingsGroup({
  id,
  title,
  count,
  actions,
  className,
  children,
  ...props
}: SettingsGroupProps) {
  const headingId = React.useId();
  return (
    <section
      data-slot="settings-group"
      id={id}
      aria-labelledby={title ? headingId : undefined}
      className={cn(
        "scroll-mt-6 overflow-hidden rounded-lg border border-border bg-card text-card-foreground",
        className,
      )}
      {...props}
    >
      {title ? (
        <div className="flex h-10 items-center gap-2 border-b border-subtle px-4">
          <h2 id={headingId} className="min-w-0 truncate text-[13px] font-semibold text-foreground">
            {title}
          </h2>
          {count !== undefined ? (
            <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
          ) : null}
          {actions ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

export type SettingsRowProps = Omit<React.ComponentProps<"div">, "title"> & {
  label: React.ReactNode;
  /** 12 px muted line under the label (one line, not a paragraph). */
  hint?: React.ReactNode;
  /** `htmlFor` of the label when the control is a single field. */
  htmlFor?: string;
  /** The control, right-aligned; `wide` puts it under the label (textareas, lists). */
  wide?: boolean;
  /** Stacks the control under the label on every width (not only phones). */
  children?: React.ReactNode;
};

/** One row of a `SettingsGroup`: label (13 / 500) and hint on the left, the control on the right. */
export function SettingsRow({
  label,
  hint,
  htmlFor,
  wide = false,
  className,
  children,
  ...props
}: SettingsRowProps) {
  const Label = htmlFor ? "label" : "span";
  return (
    <div
      data-slot="settings-row"
      className={cn(
        "flex min-h-12 gap-5 border-t border-subtle px-4 py-2.5 first:border-t-0",
        wide
          ? "flex-col gap-2"
          : "items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2",
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        <Label
          {...(htmlFor ? { htmlFor } : {})}
          className="block text-[13px] leading-5 font-medium text-foreground"
        >
          {label}
        </Label>
        {hint ? <p className="text-xs leading-4 text-muted-foreground">{hint}</p> : null}
      </div>
      {children !== undefined && children !== null ? (
        <div
          data-slot="settings-row-control"
          className={cn(
            "flex min-w-0 items-center gap-2",
            wide ? "w-full" : "shrink-0 max-md:w-full",
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export type SettingsAsideProps = Omit<React.ComponentProps<"aside">, "title"> & {
  title?: React.ReactNode;
};

/** Help column: --sunken, hairline, radius 11, padding 20, 12 px text. */
export function SettingsAside({ title, className, children, ...props }: SettingsAsideProps) {
  return (
    <aside
      data-slot="settings-aside"
      className={cn(
        "rounded-lg border border-border bg-sunken p-5 text-xs leading-[1.6] text-muted-foreground",
        className,
      )}
      {...props}
    >
      {title ? <p className="mb-1.5 text-sm font-semibold text-foreground">{title}</p> : null}
      {children}
    </aside>
  );
}

export type ToggleRowProps = Omit<React.ComponentProps<"div">, "onChange"> & {
  label: React.ReactNode;
  description?: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
};

/** Label and hint on the left, a 36 × 20 switch on the right, 16 px above and below. */
export function ToggleRow({
  label,
  description,
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  className,
  ...props
}: ToggleRowProps) {
  const id = React.useId();
  const hintId = `${id}-hint`;
  return (
    <div
      data-slot="toggle-row"
      className={cn(
        "flex items-center justify-between gap-5 border-t border-subtle py-4 first:border-t-0 first:pt-0 last:pb-0",
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
        {description ? (
          <p id={hintId} className="mt-0.5 text-xs text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <Switch
        id={id}
        aria-describedby={description ? hintId : undefined}
        {...(checked !== undefined ? { checked } : {})}
        {...(defaultChecked !== undefined ? { defaultChecked } : {})}
        {...(disabled !== undefined ? { disabled } : {})}
        onCheckedChange={(next) => onCheckedChange?.(next)}
      />
    </div>
  );
}

export type SaveDockProps = {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  message?: React.ReactNode;
  saveLabel?: string;
  /** Save is held (not saving): e.g. the change's preview is still loading or failed. */
  saveDisabled?: boolean;
  className?: string;
};

/**
 * Bottom-right while the form is dirty: --card, hairline, radius 10, --shadow-md, a 12 px
 * "Unsaved changes" note, Discard and the primary `lg` Save.
 */
export function SaveDock({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  message = "Unsaved changes",
  saveLabel = "Save changes",
  saveDisabled = false,
  className,
}: SaveDockProps) {
  if (!dirty) return null;
  return (
    <section
      data-slot="save-dock"
      aria-label="Unsaved changes"
      className={cn(
        "fixed right-6 bottom-5 z-sidebar flex items-center gap-3 rounded-[10px] border border-border bg-card py-2 pr-2 pl-4 text-xs text-muted-foreground shadow-md animate-in fade-in-0 slide-in-from-bottom-2 duration-(--dur-slow) max-md:inset-x-4 max-md:bottom-[calc(72px+env(safe-area-inset-bottom))]",
        className,
      )}
    >
      <span className="mr-auto">{message}</span>
      {onDiscard ? (
        <Button variant="ghost" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
      ) : null}
      <Button
        size="lg"
        onClick={onSave}
        disabled={saving || saveDisabled}
        aria-busy={saving || undefined}
      >
        {saving ? "Saving…" : saveLabel}
      </Button>
    </section>
  );
}

export type SaveIndicatorProps = {
  /** Set to a new value (e.g. `Date.now()`) after each successful save. */
  savedAt?: number | null;
  className?: string;
};

/** "Saved" with a check, announced politely, fading out after 1.9 s. */
export function SaveIndicator({ savedAt, className }: SaveIndicatorProps) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (!savedAt) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 1900);
    return () => clearTimeout(t);
  }, [savedAt]);
  return (
    <span
      data-slot="save-indicator"
      role="status"
      data-visible={visible || undefined}
      className={cn(
        "inline-flex items-center gap-1 text-xs text-success-text opacity-0 transition-opacity duration-(--dur-slow) data-visible:opacity-100",
        className,
      )}
    >
      {visible ? (
        <>
          <CheckIcon className="size-3.5" aria-hidden={true} />
          Saved
        </>
      ) : null}
    </span>
  );
}
